import { graphql } from 'react-relay';
import type { FC, MouseEvent } from 'react';
import React, { useEffect } from 'react';

import { SpaceOperationsFragment } from '@confluence/entity-operations/entry-points/SpaceOperationsFragment.graphql.ts';

void SpaceOperationsFragment;
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import AddIcon from '@atlaskit/icon/core/add';
import IconButton from '@atlaskit/button/icon/button';
import type { PositionType } from '@atlaskit/tooltip/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import type {
	CreateButtonBaseSpaceOperationsQuery as CreateButtonBaseSpaceOperationsQuery$data,
	CreateButtonBaseSpaceOperationsQueryVariables as CreateButtonBaseSpaceOperationsQuery$variables,
} from './__types__/CreateButtonBaseSpaceOperationsQuery.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
export type Props = {
	canCreateContent?: (permissions: CreateButtonBaseSpaceOperationsQuery$data) => boolean;
	onCreateContent?: ({ spaceId }: { spaceId: string }) => void;
	href?: string;
	labelI18n: MessageDescriptor;
	tooltipShortcut?: string;
	tooltipTestId?: string;
	spaceKey: string;
	tooltipPosition: PositionType | undefined;
	createPagePermissionCheck?: (canCreate: boolean | undefined) => (() => void) | undefined;
	testId?: string;
	/** When true, skip the internal permission query — caller supplies preloadedCanCreate instead. */
	skipQuery?: boolean;
	/** Pre-computed permission result. Used when skipQuery is true. */
	preloadedCanCreate?: boolean;
	/** Pre-loaded space ID for analytics. Used when skipQuery is true. */
	preloadedSpaceId?: string;
};

export const CreateButtonBase: FC<Props> = ({
	canCreateContent,
	href,
	onCreateContent,
	tooltipTestId,
	labelI18n,
	tooltipShortcut,
	spaceKey,
	createPagePermissionCheck,
	tooltipPosition,
	testId,
	skipQuery,
	preloadedCanCreate,
	preloadedSpaceId,
}) => {
	const renderServerPlaceholder = useIsSSROrHydrating();
	const intl = useIntl();
	const { data, loading, error } = useQueryInterop<
		CreateButtonBaseSpaceOperationsQuery$data,
		CreateButtonBaseSpaceOperationsQuery$variables
	>(
		graphql`
			query CreateButtonBaseSpaceOperationsQuery($spaceKey: String!) {
				space(key: $spaceKey) {
					...SpaceOperationsFragment @relay(mask: false)
				}
			}
		`,
		{
			variables: { spaceKey },
			skip: skipQuery,
		},
	);
	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'page-tree-create-button-base',
	});
	const { push } = useRouteActions();

	const canCreate = skipQuery ? preloadedCanCreate : data && canCreateContent?.(data);

	useEffect(() => createPagePermissionCheck?.(canCreate), [createPagePermissionCheck, canCreate]);

	const shortcutStrings = useShortcutStrings({ shortcut: tooltipShortcut });

	if (error) {
		return <ErrorDisplay error={error} />;
	}

	if (loading || !canCreate) {
		return null;
	}

	const handleButtonClick = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		event.preventDefault();

		if (href) {
			if (onCreateContent) {
				const spaceId = (skipQuery ? preloadedSpaceId : data?.space?.id) || '';
				onCreateContent({ spaceId });
			}

			if (event.metaKey) {
				window.open(href, '_blank', 'noreferrer');
			} else {
				push(href);
			}
		}
	};

	return (
		<Tooltip
			content={intl.formatMessage(labelI18n)}
			shortcut={shortcutStrings}
			position={tooltipPosition}
			testId={tooltipTestId}
		>
			<IconButton
				icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
				label={intl.formatMessage(labelI18n)}
				onClick={enforceStorageLimit(handleButtonClick)}
				testId={testId}
				spacing="compact"
				appearance="subtle"
				isDisabled={renderServerPlaceholder}
			/>
		</Tooltip>
	);
};
